/**
 * Validation utilities for email and phone validation
 */

// List of common disposable email domains to block
const DISPOSABLE_DOMAINS = [
  'tempmail.com', 'throwaway.email', 'mailinator.com', 'guerrillamail.com',
  'temp-mail.org', '10minutemail.com', 'fakeinbox.com', 'trashmail.com'
];

// Valid email domains (common providers + business domains)
const VALID_TLDs = [
  'com', 'org', 'net', 'edu', 'gov', 'in', 'co.in', 'org.in', 'gov.in',
  'ac.in', 'res.in', 'mil.in', 'io', 'co', 'ai', 'app', 'dev', 'tech',
  'info', 'biz', 'us', 'uk', 'eu', 'de', 'fr', 'jp', 'au', 'ca'
];

/**
 * Validate email format and domain
 * @param {string} email - Email address to validate
 * @returns {{ isValid: boolean, error: string | null }}
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim().toLowerCase();

  // Basic format check using regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Extract domain
  const parts = trimmedEmail.split('@');
  if (parts.length !== 2) {
    return { isValid: false, error: 'Invalid email format' };
  }

  const domain = parts[1];
  
  // Check for disposable email domains
  if (DISPOSABLE_DOMAINS.some(d => domain.includes(d))) {
    return { isValid: false, error: 'Disposable email addresses are not allowed' };
  }

  // Check TLD validity
  const domainParts = domain.split('.');
  const tld = domainParts.slice(-2).join('.'); // For compound TLDs like co.in
  const simpleTld = domainParts[domainParts.length - 1];
  
  if (!VALID_TLDs.includes(tld) && !VALID_TLDs.includes(simpleTld)) {
    // Allow it but we can't validate all TLDs
  }

  // Check for common typos in popular domains
  const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'rediffmail.com'];
  const typoSuggestions = {
    'gmial.com': 'gmail.com',
    'gmal.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'yaho.com': 'yahoo.com',
    'yahooo.com': 'yahoo.com',
    'hotmal.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
  };

  if (typoSuggestions[domain]) {
    return { 
      isValid: false, 
      error: `Did you mean ${parts[0]}@${typoSuggestions[domain]}?` 
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validate Indian phone number format
 * @param {string} phone - Phone number to validate
 * @returns {{ isValid: boolean, error: string | null, formatted: string | null }}
 */
export const validateIndianPhone = (phone) => {
  if (!phone || phone.trim() === '') {
    return { isValid: false, error: 'Phone number is required', formatted: null };
  }

  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // Remove leading + for processing
  const hasPlus = cleaned.startsWith('+');
  if (hasPlus) {
    cleaned = cleaned.substring(1);
  }

  // Remove leading 91 if present (country code)
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    cleaned = cleaned.substring(2);
  }

  // Remove leading 0 if present
  if (cleaned.startsWith('0') && cleaned.length === 11) {
    cleaned = cleaned.substring(1);
  }

  // Now we should have a 10-digit number
  if (cleaned.length !== 10) {
    return { 
      isValid: false, 
      error: 'Please enter a valid 10-digit Indian mobile number',
      formatted: null 
    };
  }

  // Check if it starts with valid Indian mobile prefixes (6, 7, 8, 9)
  const firstDigit = cleaned.charAt(0);
  if (!['6', '7', '8', '9'].includes(firstDigit)) {
    return { 
      isValid: false, 
      error: 'Indian mobile numbers must start with 6, 7, 8, or 9',
      formatted: null 
    };
  }

  // Check for obviously invalid patterns (all same digits, sequential)
  if (/^(\d)\1{9}$/.test(cleaned)) {
    return { 
      isValid: false, 
      error: 'Please enter a valid phone number',
      formatted: null 
    };
  }

  // Format with country code
  const formatted = `+91 ${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;

  return { isValid: true, error: null, formatted };
};

/**
 * Validate password strength (market standard requirements)
 * @param {string} password - Password to validate
 * @returns {{ isValid: boolean, error: string | null, strength: string, requirements: object }}
 */
export const validatePassword = (password) => {
  if (!password || password.length === 0) {
    return { isValid: false, error: 'Password is required', strength: 'none', requirements: {} };
  }

  const requirements = {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password)
  };

  const missingRequirements = [];
  if (!requirements.minLength) missingRequirements.push('at least 8 characters');
  if (!requirements.uppercase) missingRequirements.push('one uppercase letter');
  if (!requirements.lowercase) missingRequirements.push('one lowercase letter');
  if (!requirements.number) missingRequirements.push('one number');
  if (!requirements.special) missingRequirements.push('one special character (!@#$%^&*)');

  const isValid = missingRequirements.length === 0;
  const error = isValid ? null : `Password must contain ${missingRequirements.join(', ')}`;

  // Calculate strength
  const metCount = Object.values(requirements).filter(Boolean).length;
  let strength = 'weak';
  if (metCount >= 5) {
    strength = 'strong';
  } else if (metCount >= 3) {
    strength = 'medium';
  }

  return { isValid, error, strength, requirements };
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  
  const result = validateIndianPhone(phone);
  return result.formatted || phone;
};
